import { useEffect, useState } from "react";
import { getCachedUserBundle } from "../../services/authService";
import { buildApiUrl, createHeaders } from "../../services/apiClient";
import { parseThemeIds } from "../utils/profileUtils";

export const useCachedProfile = () => {
  const [profile, setProfile] = useState(null);
  const [user, setUser] = useState(null);
  const [userThemeIds, setUserThemeIds] = useState([]);

  // 1) Seed instantly from the local cache so the dashboard renders without a
  //    round-trip. The cache deliberately omits theme selections (see
  //    Profile.js handleSave), so themes here may be empty — the refresh below
  //    fills them in.
  useEffect(() => {
    const cached = getCachedUserBundle();
    if (cached) {
      const profileData = cached.profile || null;
      const userData = cached.user || null;
      if (profileData) setProfile(profileData);
      if (userData) setUser(userData);
      const themeSource = userData?.themes || profileData?.themes || null;
      const initialIds = parseThemeIds(themeSource);
      setUserThemeIds(initialIds);
    }
  }, []);

  // 2) Refresh from the server. Because the cached bundle does not persist
  //    themes, profile completeness (which counts profile.themes) would
  //    otherwise stay stuck at "pick a theme" even after the user saved themes.
  //    Fetch the authoritative profile so themes reflect auth.users.themes.
  useEffect(() => {
    let cancelled = false;
    const cached = getCachedUserBundle();
    const uid =
      cached?.user?.id ||
      cached?.profile?.id ||
      Number(localStorage.getItem("userId")) ||
      null;
    if (!uid) return undefined;

    (async () => {
      try {
        const res = await fetch(buildApiUrl("/wineries/profile"), {
          headers: createHeaders({ json: true, userId: uid }),
        });
        if (!res.ok) return;
        const data = await res.json().catch(() => null);
        if (cancelled || !data) return;
        if (data.profile) setProfile(data.profile);
        if (data.user) setUser(data.user);
        const themeSource = data.user?.themes || data.profile?.themes || null;
        setUserThemeIds(parseThemeIds(themeSource));
      } catch {
        // Keep the cached values on any network/parse error.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return {
    profile,
    setProfile,
    user,
    setUser,
    userThemeIds,
    setUserThemeIds,
  };
};
